import {
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  Input,
  OnDestroy,
  Renderer2,
  type OnInit,
} from '@angular/core';
import { TopbarComponent } from '../topbar/topbar.component';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { FooterComponent } from '../footer/footer.component';
import { Router, RouterModule } from '@angular/router';
import {
  NgbModal,
  NgbOffcanvas,
  NgbOffcanvasModule,
} from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { getSidebarsize } from '@/store/layout/layout-selector';
import { changesidebarsize } from '@/store/layout/layout-action';
import type { LayoutState } from '@/store/layout/layout-reducers';
import { RightSidebarComponent } from '../right-sidebar/right-sidebar.component';
import { getisLoggedIn } from '@/store/authentication/authentication.selector';
import { EmployeeActions } from '@/store/employee/employee.actions';
import { getUserProfile } from '@/store/employee/employee.selectors';
import { AuthenticationService } from '@core/services/auth.service';
import { Subscription } from 'rxjs';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { provideNgIdle } from '@ng-idle/core';
import { logout } from '@/store/authentication/authentication.actions';

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    TopbarComponent,
    SidebarComponent,
    FooterComponent,
    RouterModule,
    NgbOffcanvasModule,
  ],
  providers: [
    provideNgIdle(),
    Keepalive, // Provide Keepalive service
  ],
  templateUrl: './main-layout.component.html',
  styles: ``,
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  private authService = inject(AuthenticationService);
  private modalService = inject(NgbModal);
  private router = inject(Router);
  private idle = inject(Idle);
  private keepalive = inject(Keepalive);
  @Input() title!: string;
  @Input() subTitle!: string;
  private store = inject(Store);
  private offcanvasService = inject(NgbOffcanvas);
  private renderer = inject(Renderer2);
  subscription: Subscription = new Subscription();
  private cdr = inject(ChangeDetectorRef);
  idleState = 'Not started.';
  timedOut = false;
  lastPing?: Date;

  onSettingsButtonClicked() {
    this.offcanvasService.open(RightSidebarComponent, {
      position: 'end',
      backdrop: true,
    });
  }

  onToggleMobileMenu() {
    this.store.select(getSidebarsize).subscribe((size: any) => {
      document.documentElement.setAttribute('data-sidenav-size', size);
    });

    const size = document.documentElement.getAttribute('data-sidenav-size');

    document.documentElement.classList.toggle('sidebar-enable');
    if (size != 'full') {
      if (document.documentElement.classList.contains('sidebar-enable')) {
        this.store.dispatch(changesidebarsize({ size: 'condensed' }));
      } else {
        this.store.dispatch(changesidebarsize({ size: 'default' }));
      }
    } else {
      this.showBackdrop();
    }
  }

  showBackdrop() {
    const backdrop = this.renderer.createElement('div');
    this.renderer.addClass(backdrop, 'offcanvas-backdrop');
    this.renderer.addClass(backdrop, 'fade');
    this.renderer.addClass(backdrop, 'show');
    this.renderer.appendChild(document.body, backdrop);
    this.renderer.setStyle(document.body, 'overflow', 'hidden');

    if (window.innerWidth > 1040) {
      this.renderer.setStyle(document.body, 'paddingRight', '15px');
    }

    this.renderer.listen(backdrop, 'click', () => {
      document.documentElement.classList.remove('sidebar-enable');
      this.renderer.removeChild(document.body, backdrop);
      this.renderer.setStyle(document.body, 'overflow', null);
      this.renderer.setStyle(document.body, 'paddingRight', null);
    });
  }
  @HostListener('window:resize', ['$event'])
  Resizing() {
    this.store.select('layout').subscribe((data: LayoutState) => {
      document.documentElement.setAttribute('data-bs-theme', data.LAYOUT_THEME);
      document.documentElement.setAttribute('data-bs-theme', data.LAYOUT_THEME);

      document.documentElement.setAttribute('data-menu-color', data.MENU_COLOR);
      document.documentElement.setAttribute(
        'data-topbar-color',
        data.TOPBAR_COLOR
      );
      document.documentElement.setAttribute(
        'data-sidenav-size',
        data.MENU_SIZE
      );
    });
    if (document.documentElement.clientWidth <= 1140) {
      this.onResize();
    }
  }

  ngOnInit(): void {
    this.Resizing();
    this.subscription.add(
      this.store.select(getisLoggedIn).subscribe((isLoggedIn) => {
        if (isLoggedIn) {
          this.store.dispatch(EmployeeActions.employeeProfile());
          this.store.select(getUserProfile).subscribe((profile) => {
            if (profile) this.authService.setUser(profile);
          });
        }
      })
    );
    this.setIdleSession();
  }

  onResize() {
    if (
      document.documentElement.clientWidth <= 1140 &&
      document.documentElement.clientWidth >= 768
    ) {
      this.store.dispatch(changesidebarsize({ size: 'condensed' }));
    } else if (document.documentElement.clientWidth <= 768) {
      this.store.dispatch(changesidebarsize({ size: 'full' }));
    } else {
      this.store.dispatch(changesidebarsize({ size: 'default' }));
      document.documentElement.classList.remove('sidebar-enable');
      const backdrop = document.querySelector('.offcanvas-backdrop');
      if (backdrop) this.renderer.removeChild(document.body, backdrop);
    }

    this.store.select(getSidebarsize).subscribe((size: string) => {
      this.renderer.setAttribute(
        document.documentElement,
        'data-sidenav-size',
        size
      );
    });
  }

  onActivate(componentReference: any) {
    this.subTitle = componentReference.subTitle;
    this.title = componentReference.title;
    this.cdr.detectChanges();
  }
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.modalService.hasOpenModals()) {
      this.modalService.dismissAll();
    }
  }

  setIdleSession() {
    this.idle.setIdle(900); // 15 minutes of inactivity
    this.idle.setTimeout(60); // 1 minute countdown
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES); // Reset on user activity

    // Subscribe to idle events
    this.idle.onIdleStart.subscribe(() => {
      this.idleState = "You've gone idle!";
    });
    this.idle.onTimeoutWarning.subscribe((countdown) => {
      this.idleState = 'You will time out in ' + countdown + ' seconds!';
    });
    this.idle.onTimeout.subscribe(() => {
      this.idleState = 'Timed out!';
      this.store.dispatch(EmployeeActions.employeesLogout());
      this.store.dispatch(logout());
      this.timedOut = true;
    });
    this.idle.onIdleEnd.subscribe(() => {
      this.idleState = 'No longer idle. Resetting session timer.';
      this.resetSession(); // Restart session timer
    });

    // Ping server to keep session alive
    this.keepalive.interval(15); // Ping every 15 seconds
    this.keepalive.onPing.subscribe(() => (this.lastPing = new Date()));

    // Start watching for idle activity
    this.resetSession();
  }
  resetSession(): void {
    this.idle.watch();
    this.idleState = 'Session restarted.';
    this.timedOut = false;
  }
}
